import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export type DateRangePreset =
  | 'Last day'
  | 'Last week'
  | 'Last month'
  | 'Current day'
  | 'Current week'
  | 'Current month'
  | 'Next day'
  | 'Next week'
  | 'Next month'
  | 'Last 30 days'
  | 'Custom';

type Unit = 'day' | 'week' | 'month';

const presetConfigs: Record<string, { offset: number; unit: Unit }> = {
  'Last day': { offset: -1, unit: 'day' },
  'Last week': { offset: -1, unit: 'week' },
  'Last month': { offset: -1, unit: 'month' },
  'Current day': { offset: 0, unit: 'day' },
  'Current week': { offset: 0, unit: 'week' },
  'Current month': { offset: 0, unit: 'month' },
  'Next day': { offset: 1, unit: 'day' },
  'Next week': { offset: 1, unit: 'week' },
  'Next month': { offset: 1, unit: 'month' },
};

export function calculateDateRange(preset: string): { startDate: Date; endDate: Date } {
  if (preset === 'Last 30 days') {
    const today = dayjs();
    return {
      startDate: today.subtract(29, 'day').startOf('day').toDate(),
      endDate: today.endOf('day').toDate(),
    };
  }

  const config = presetConfigs[preset];

  if (!config) {
    console.warn(`Unknown date range preset: ${preset}, defaulting to Current week`);
    return calculateDateRange('Current week');
  }

  const { offset, unit } = config;
  const target = dayjs().add(offset, unit);
  // week boundaries are Monday-based (isoWeek)
  const boundary = unit === 'week' ? 'isoWeek' : unit;

  return {
    startDate: target.startOf(boundary).toDate(),
    endDate: target.endOf(boundary).toDate(),
  };
}

/**
 * Format date for backend (YYYY-MM-DD format)
 * @param date - Date object to format
 * @returns Formatted date string
 */
export function formatDateForBackend(date: Date): string {
  return dayjs(date).format('YYYY-MM-DD');
}
